import { Sport } from "@sportactics/board-editing";
import { OfflineError } from "@sportactics/errors";
import { Team } from "@sportactics/player-management";
import { useEffect, useEffectEvent } from "react";
import { useSWRConfig } from "swr";

import { swallow } from "~utils";

import { useUser } from "../user";
import {
  useCustomPlayersFirestorePersistence,
  useCustomPlayersFirestorePersistenceReturn,
} from "./custom-players-firestore-persistence";
import {
  TeamNotFoundError,
  useCustomPlayersLocalPersistence,
  useCustomPlayersLocalPersistenceReturn,
} from "./custom-players-local-persistence";
import { getTeamsFirestoreSWRKey } from "./use-teams-firestore-swr";
import { getTeamsLocalSWRKey } from "./use-teams-local-swr";

// Same once-per-sign-in shape as `useMigrateBoardsEffect`.
export function useMigrateTeamsEffect(): void {
  const { user } = useUser();
  const migrate = useEffectEvent(useMigrate());

  useEffect(() => {
    if (!user.authenticated) return;

    void swallow(migrate, OfflineError)();
  }, [user.authenticated, user.id]);
}

function useMigrate(): () => Promise<void> {
  const { user } = useUser();
  const { mutate } = useSWRConfig();
  const local = useCustomPlayersLocalPersistence();
  const firestore = useCustomPlayersFirestorePersistence();

  const userId = user.id;

  return async (): Promise<void> => {
    await Promise.all(
      Object.values(Sport).map(async (sport) => {
        const teams = await local.getTeamsWithSport(sport);
        if (teams.length === 0) return;

        await Promise.all(
          teams.map((team) => migrateTeam(userId, team, firestore, local)),
        );

        // The account's list gained the migrated teams, so an open list re-reads
        // it. The device's list lost them, and the local store is shared by every
        // anonymous user of the device: it must not resurface for the next one.
        await Promise.all([
          mutate(getTeamsFirestoreSWRKey(userId, sport)),
          mutate(getTeamsLocalSWRKey(sport), [], { revalidate: false }),
        ]);
      }),
    );
  };
}

// A team the account already holds is left as is: its id is random, so a match can
// only be an earlier run that wrote it but did not get to delete the local copy.
// The local copy is deleted only once the Firestore write has actually landed.
async function migrateTeam(
  userId: string,
  team: Team,
  firestore: useCustomPlayersFirestorePersistenceReturn,
  local: useCustomPlayersLocalPersistenceReturn,
): Promise<void> {
  const existing = await swallow(firestore.getTeamWithId, TeamNotFoundError)(
    userId,
    team.id,
  );
  if (existing === undefined) await firestore.createTeam(userId, team);
  await local.deleteTeam(team.id);
}
